import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fileToBase64 } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText, Input } from '../components/ui.js';
import { eur, reasonText, VOUCHER_STATUS, type StatementStatus, type VoucherStatus } from '../format.js';
import { TAX_MODE_TEXT } from './PartnersPage.js';

export interface InvoiceVoucher {
  id: string;
  status: VoucherStatus;
  voucherType: string;
  voucherNumber: string;
  voucherId: string | null;
  files: Array<{ documentId: string; fileId: string }>;
  lastError: string | null;
  lexwareVoucherStatus: string | null;
  paidOn: string | null;
}

export interface PartnerInvoice {
  id: string;
  status: 'open' | 'assigned' | 'dismissed';
  statementId: string | null;
  statementNumber: string | null;
  partnerId: string | null;
  partnerName: string | null;
  filename: string;
  source: 'mail' | 'upload';
  fromAddress: string | null;
  subject: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  amountEur: string | null;
  payoutEur: string | null;
  matchedBy: string | null;
  unmatchedReason: string | null;
  differenceAcceptedAt: string | null;
  differenceAcceptedBy: string | null;
  differenceReason: string | null;
  dismissalReason: string | null;
  receivedAt: string;
  senderAuth: 'dmarc_pass' | 'dkim_aligned' | 'forwarded' | 'failed' | 'none' | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  taxMode: 'de_standard' | 'kleinunternehmer' | 'reverse_charge' | null;
  amountCheck: { state: 'matches' | 'differs' | 'unknown'; differenceEur: string | null; expectedEur: string } | null;
  voucher: InvoiceVoucher | null;
}

export interface StatementInvoiceState {
  statementId: string;
  status: StatementStatus;
  payoutEur: string;
  paidOn: string | null;
  awaitingInvoice: boolean;
  sentAt: string | null;
  reminderDueAt: string | null;
  invoiceRemindedAt: string | null;
  invoice: PartnerInvoice | null;
}

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.message ?? e.code) : fallback);
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '-');
const SENDER_AUTH: Record<string, string> = {
  dmarc_pass: 'DMARC passed',
  dkim_aligned: 'DKIM of the sender domain passed',
  forwarded: 'forwarded by SilentFuture',
  failed: 'failed',
  none: 'not authenticated',
};
const MATCHED_BY: Record<string, string> = {
  reference: 'reply to the statement mail',
  statement_number: 'statement number in the PDF',
  amount: 'partner and amount',
  single_open_statement: 'the only statement waiting',
  manual: 'assigned by hand',
  upload: 'uploaded on this page',
};

export function AmountCheck({ invoice }: { invoice: Pick<PartnerInvoice, 'amountCheck' | 'differenceAcceptedAt' | 'differenceReason'> }) {
  const c = invoice.amountCheck;
  if (!c) return null;
  if (c.state === 'matches') return <span className="text-emerald-700 dark:text-emerald-400">matches the expected total {eur(c.expectedEur)}</span>;
  if (c.state === 'unknown') return <span className="text-amber-700 dark:text-amber-400">amount unknown: enter it</span>;
  return (
    <span className="font-semibold text-red-700 dark:text-red-400">
      differs from the expected total {eur(c.expectedEur)} by {eur(c.differenceEur)}
      {invoice.differenceAcceptedAt ? ` (accepted: ${invoice.differenceReason})` : ''}
    </span>
  );
}

/**
 * The partner's invoice of a statement (STE-11): whether one is awaited, the invoice with its amount check, the
 * Lexware voucher and the payment. Admins upload an invoice that arrived elsewhere, correct number, date and amount
 * before booking, accept a difference with a reason or take the invoice off the statement.
 */
export function StatementInvoice({ statementId, isHouse }: { statementId: string; isHouse: boolean }) {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const state = useQuery({ queryKey: ['statements', 'invoice', statementId], queryFn: () => api<StatementInvoiceState>('GET', `/api/statements/${statementId}/invoice`) });
  const refresh = () => Promise.all(['statements', 'payments', 'invoices'].map((k) => client.invalidateQueries({ queryKey: [k] })));
  const [file, setFile] = useState<File | null>(null);
  const [details, setDetails] = useState<{ invoiceNumber?: string; invoiceDate?: string; amountEur?: string }>({});
  const [reason, setReason] = useState('');
  const upload = useMutation({
    mutationFn: async () => api('POST', `/api/statements/${statementId}/invoice`, { filename: file!.name, contentBase64: await fileToBase64(file!) }),
    onSuccess: async () => {
      setFile(null);
      await refresh();
    },
  });
  const inv = state.data?.invoice ?? null;
  const save = useMutation({
    mutationFn: () => api('PATCH', `/api/partner-invoices/${inv!.id}`, details),
    onSuccess: async () => {
      setDetails({});
      await refresh();
    },
  });
  const accept = useMutation({ mutationFn: () => api('POST', `/api/partner-invoices/${inv!.id}/accept-difference`, { reason }), onSuccess: refresh });
  const acceptInvoice = useMutation({ mutationFn: () => api('POST', `/api/partner-invoices/${inv!.id}/accept`), onSuccess: refresh });
  const unassign = useMutation({ mutationFn: () => api('POST', `/api/partner-invoices/${inv!.id}/unassign`), onSuccess: refresh });
  if (isHouse) return null;
  if (state.isError) return <ErrorText>{errorText(state.error, 'The invoice could not be loaded')}</ErrorText>;
  const s = state.data;
  if (!s) return null;
  const editable = isAdmin && inv && (!inv.voucher || ['pending', 'failed'].includes(inv.voucher.status));
  const error = upload.error ?? save.error ?? accept.error ?? acceptInvoice.error ?? unassign.error;
  return (
    <div className="space-y-2">
      <h2 className="font-medium">Partner invoice</h2>
      {!inv && s.awaitingInvoice && (
        <p className="text-sm">
          Waiting for the partner&apos;s invoice{s.sentAt ? ` since the mail of ${day(s.sentAt)}` : ''}
          {s.reminderDueAt ? `; reminder to Daniel from ${day(s.reminderDueAt)}` : ''}
          {s.invoiceRemindedAt ? ` (last reminder ${day(s.invoiceRemindedAt)})` : ''}. Partners send it to the billing mailbox, where it is matched by sender and amount.
        </p>
      )}
      {!inv && !s.awaitingInvoice && <p className="text-sm text-slate-500">No invoice.</p>}
      {inv && (
        <dl className="grid max-w-2xl grid-cols-[14rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-500">Invoice</dt>
          <dd>
            <a className="underline" href={`/api/partner-invoices/${inv.id}/pdf?inline=1`} target="_blank" rel="noreferrer">{inv.filename}</a>
            {' '}({inv.source === 'mail' ? `mail from ${inv.fromAddress ?? 'unknown'}, sender check ${SENDER_AUTH[inv.senderAuth ?? 'none']}` : 'uploaded'}, matched by {MATCHED_BY[inv.matchedBy ?? ''] ?? inv.matchedBy ?? '-'})
          </dd>
          <dt className="text-slate-500">Accepted</dt>
          <dd className={inv.acceptedAt ? '' : 'font-semibold text-amber-700 dark:text-amber-400'}>
            {inv.acceptedAt ? `${day(inv.acceptedAt)} by ${inv.acceptedBy ?? '-'}` : 'not yet: an admin checks the PDF and accepts it before it is booked'}
            {isAdmin && !inv.acceptedAt && <Button className="ml-3" onClick={() => acceptInvoice.mutate()} disabled={acceptInvoice.isPending}>Accept invoice</Button>}
          </dd>
          <dt className="text-slate-500">Number and date</dt>
          <dd>{inv.invoiceNumber ?? 'missing'}, {inv.invoiceDate ?? 'date missing'}</dd>
          <dt className="text-slate-500">Amount</dt>
          <dd>{inv.amountEur ? eur(inv.amountEur) : 'missing'} for the payout {eur(s.payoutEur)}{inv.taxMode ? ` (${TAX_MODE_TEXT[inv.taxMode]})` : ''}: <AmountCheck invoice={inv} /></dd>
          <dt className="text-slate-500">Lexware</dt>
          <dd>
            {inv.voucher ? (
              <>
                <span className={VOUCHER_STATUS[inv.voucher.status].className}>{VOUCHER_STATUS[inv.voucher.status].label}</span> {inv.voucher.voucherType} {inv.voucher.voucherNumber}
                {inv.voucher.files.length ? `, ${inv.voucher.files.length} file(s)` : ''}
                {inv.voucher.lastError ? <span className="text-red-700 dark:text-red-400">: {inv.voucher.lastError}</span> : null}
              </>
            ) : (
              'not booked yet'
            )}
          </dd>
          {s.paidOn && (
            <>
              <dt className="text-slate-500">Paid</dt>
              <dd className="text-emerald-700 dark:text-emerald-400">{s.paidOn}</dd>
            </>
          )}
        </dl>
      )}
      {editable && (
        <div className="space-y-3">
          <form className="flex max-w-3xl flex-wrap items-end gap-2" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
            <div className="w-48"><Input label="Invoice number" value={details.invoiceNumber ?? inv.invoiceNumber ?? ''} onChange={(e) => setDetails({ ...details, invoiceNumber: e.target.value })} /></div>
            <div className="w-40"><Input label="Invoice date" type="date" value={details.invoiceDate ?? inv.invoiceDate ?? ''} onChange={(e) => setDetails({ ...details, invoiceDate: e.target.value })} /></div>
            <div className="w-36"><Input label="Amount (EUR)" inputMode="decimal" value={details.amountEur ?? inv.amountEur ?? ''} onChange={(e) => setDetails({ ...details, amountEur: e.target.value })} /></div>
            <Button type="submit" disabled={save.isPending || Object.keys(details).length === 0}>Save details</Button>
            <Button type="button" className="bg-slate-500" onClick={() => unassign.mutate()} disabled={unassign.isPending}>Take off this statement</Button>
          </form>
          {inv.amountCheck?.state === 'differs' && !inv.differenceAcceptedAt && (
            <form className="max-w-2xl space-y-2" onSubmit={(e) => { e.preventDefault(); accept.mutate(); }}>
              <label className="block text-sm">
                <span className="mb-1 block text-slate-600 dark:text-slate-400">Accept the difference of {eur(inv.amountCheck.differenceEur)} (reason; the invoice is booked with its own amount)</span>
                <textarea className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
              <Button type="submit" disabled={accept.isPending || reason.trim().length < 10}>Accept difference</Button>
            </form>
          )}
        </div>
      )}
      {isAdmin && !inv && s.awaitingInvoice && (
        <form className="flex items-end gap-2" onSubmit={(e: FormEvent) => { e.preventDefault(); upload.mutate(); }}>
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Upload the invoice (PDF, up to 5 MB)</span>
            <input type="file" accept="application/pdf,.pdf" aria-label="Invoice PDF" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          <Button type="submit" disabled={!file || upload.isPending}>Upload</Button>
        </form>
      )}
      {inv?.unmatchedReason && <p className="text-sm text-slate-500">{reasonText(inv.unmatchedReason)}</p>}
      {error && <ErrorText>{errorText(error, 'The change failed')}</ErrorText>}
    </div>
  );
}
